export type QualityTier = 'high' | 'medium' | 'low';

export interface QualitySettings {
  tier: QualityTier;
  maxPixelRatio: number;
  /** WebGL context param — fixed at renderer construction (changing it live means recreating the GL context), never touched by setQualityTier. */
  antialias: boolean;
  /** Camera far plane — S08's real draw-distance lever, live-adjustable via setQualityTier. */
  cameraFar: number;
}

const TIERS: Record<QualityTier, QualitySettings> = {
  high: { tier: 'high', maxPixelRatio: 2, antialias: true, cameraFar: 200 },
  medium: { tier: 'medium', maxPixelRatio: 1.5, antialias: true, cameraFar: 140 },
  low: { tier: 'low', maxPixelRatio: 1, antialias: false, cameraFar: 100 },
};

export const QUALITY_TIERS: QualityTier[] = ['low', 'medium', 'high'];

export function getQualitySettings(tier: QualityTier): QualitySettings {
  return TIERS[tier];
}

/**
 * Real device-capability detection (S08, PLAN-CITY §5 — S00 only pinned this
 * module's shape). A cheap heuristic, not a render benchmark: `hardwareConcurrency`
 * and `deviceMemory` (Chrome/Android only — undefined on iOS Safari/Firefox,
 * so its absence must NOT be scored as "low-end," or every non-Chrome mobile
 * browser would wrongly land on the lowest tier) are the two standard signals
 * real web games use to avoid an actual startup perf probe. `pointer: coarse`
 * distinguishes a touch-primary device (phone/tablet) from a mouse/trackpad
 * one (very likely a desktop dev machine, which can afford 'high' regardless
 * of core count).
 *
 * S08 spec-fidelity review finding (MEDIUM): an earlier draft also scored a
 * high native `devicePixelRatio` (3x+, common on flagship Android — including
 * this project's own named test phone, the Samsung S25 Ultra) as a penalty,
 * reasoning it "costs real fill-rate even on an otherwise-capable chip." That
 * reasoning double-counted a cost `getInitialQuality()`'s OWN caller already
 * pays for downstream: `renderer.setPixelRatio(Math.min(devicePixelRatio,
 * quality.maxPixelRatio))` (main.ts) clamps the ACTUAL rendered pixel count to
 * `maxPixelRatio` regardless of which tier produced it — a dpr=4 phone tiered
 * 'high' renders at exactly 2x, the same real cost as a dpr=2 phone tiered
 * 'high'. Penalizing the TIER SCORE for something the tier's own downstream
 * clamp already bounds made 'high' structurally unreachable for most real
 * touch devices (score capped at 3 of the 4 needed) — backwards for a
 * mobile-hardening feature whose whole point is not shortchanging capable
 * phones. Dropped entirely; `maxPixelRatio`'s clamp is the correct, sole
 * place high-dpr cost gets bounded.
 */
export function detectQualityTier(): QualityTier {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const cores = nav.hardwareConcurrency ?? 4;
  const memory = nav.deviceMemory;
  const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

  let score = 0;
  if (cores >= 8) score += 2;
  else if (cores >= 4) score += 1;

  if (memory !== undefined) {
    if (memory >= 6) score += 2;
    else if (memory >= 4) score += 1;
  } else {
    // S08 don't-break-list review finding (MEDIUM): `deviceMemory` is a
    // Chromium-only API — its absence means "this is Safari/Firefox," not
    // "this is a weak device," and conflating the two silently downgraded a
    // real, common desktop/laptop class (any non-Chromium browser reporting
    // ≤4 cores, e.g. any pre-Apple-Silicon Mac) from 'high' to 'medium' —
    // a rendering-quality regression nobody asked for, since the pre-S08
    // stub unconditionally returned 'high' for every machine. +2, not +1:
    // matches the BEST-case known-memory score (>=6GB) rather than the
    // worst, since a non-Chromium desktop is far more common in the wild
    // than a genuinely low-end unknown-memory machine.
    score += 2;
  }

  if (!coarsePointer) score += 1; // mouse/trackpad — very likely desktop

  if (score >= 4) return 'high';
  if (score >= 2) return 'medium';
  return 'low';
}

export function getInitialQuality(): QualitySettings {
  return TIERS[detectQualityTier()];
}
